import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { COUNTRY_SETTINGS } from "@/config/country-settings";
import { dwellingRequiresCost } from "@/constants/dwelling-categories";
import {
	employmentRequiresStartDate,
	employmentRequiresWorkDetails,
} from "@/constants/employment-categories";
import { getEmploymentDateBounds } from "@/constants/employment-date-bounds";
import { useRuntimeConfig } from "@/contexts/runtime-config";
import { isAutofillEnabled } from "@/dev/console/autofill-preference";
import {
	getMockCoApplicantContact,
	getMockEmploymentDefaults,
} from "@/dev/console/data/mock-contact-values";
import {
	useCoApplicantContactSchema,
	useValidationCountry,
} from "@/hooks/form/use-contact-field-schemas";
import { useCivilApplicantSchema } from "@/hooks/form/use-personal-information-form";
import { useAppCountry, useCountrySettings } from "@/hooks/use-app-country";
import {
	Applicant,
	Debts,
	Dwelling,
	DwellingType,
	Employment,
	EmploymentType,
	Expense,
	ExpenseType,
	Income,
	IncomeType,
	Period,
} from "@/types/form";
import type { NationalIdCountry } from "@/utils/validation/normalize-national-id";

export interface OnlyEconomyForm {
	dwelling: {
		type: Dwelling["type"];
		expenses: Expense;
	};
	employment: Employment;
	income: Income;
	debts?: Debts;
}

type ApplicantForm = {
	economy: OnlyEconomyForm;
};

export type CoApplicantForm = {
	applicant: Applicant;
	economy: OnlyEconomyForm;
};

export interface EconomyForm {
	applicant: ApplicantForm;
	coApplicant?: CoApplicantForm;
	hasCoApplicant?: boolean;
}

export type EmploymentEconomyFields = Pick<
	OnlyEconomyForm,
	"employment" | "income"
>;

export type EmploymentCarsFields = {
	employment: Pick<Employment, "numberOfCars">;
};

export type DebtEconomyFields = {
	debts: Debts;
};

export type ResidenceEconomyFields = Pick<OnlyEconomyForm, "dwelling">;

export const emptyCoApplicant = (
	mobileCountry: NationalIdCountry
): CoApplicantForm =>
	({
		applicant: { mobileCountry },
		economy: {
			dwelling: {
				expenses: {
					type: ExpenseType.HousingExpenses,
					period: Period.Monthly,
				},
			},
			income: {
				type: IncomeType.GrossIncome,
				period: Period.Monthly,
			},
			employment: {},
			debts: {
				mortgageLoan: undefined,
				vehicleLoan: undefined,
				studentLoan: undefined,
				consumerLoan: undefined,
			},
		},
	}) as CoApplicantForm;

export const useEmploymentPartialSchema = () => {
	const t = useTranslations("forms.errors.general");
	const tEconomy = useTranslations("forms.errors.economyForm");
	const { minMonthlyIncome: minIncome } = useCountrySettings();
	const { minDate: minEmploymentStartDate, maxDate: maxEmploymentStartDate } =
		getEmploymentDateBounds();

	return z
		.object({
			employment: z.object({
				type: z.nativeEnum(EmploymentType, { message: t("required") }),
				employer: z.string().optional(),
				startDate: z
					.date({
						message: tEconomy("invalidEmploymentStartDate"),
					})
					.min(minEmploymentStartDate, {
						message: tEconomy("employmentStartDateOutOfRange"),
					})
					.max(maxEmploymentStartDate, {
						message: tEconomy("employmentStartDateOutOfRange"),
					})
					.optional(),
				numberOfCars: z.number().int().min(0).max(100).optional(),
			}),
			income: z.object({
				amount: z
					.number()
					.max(100000000)
					.min(minIncome, { message: tEconomy("incomeTooLow") })
					.optional(),
				type: z.nativeEnum(IncomeType, { message: t("required") }),
				period: z.nativeEnum(Period),
			}),
		})
		.superRefine((data, ctx) => {
			if (typeof data.income.amount !== "number") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["income", "amount"],
				});
			}

			if (
				employmentRequiresWorkDetails(data.employment.type) &&
				!data.employment.employer
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "employer"],
				});
			}

			if (
				employmentRequiresStartDate(data.employment.type) &&
				!data.employment.startDate
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "startDate"],
				});
			}
		}) satisfies z.ZodType<EmploymentEconomyFields>;
};

export const useEmploymentCarsPartialSchema = () => {
	const t = useTranslations("forms.errors.general");
	const { collectsCarsAndDebt } = useCountrySettings();

	return z
		.object({
			employment: z.object({
				numberOfCars: z.number().int().min(0).max(100),
			}),
		})
		.superRefine((data, ctx) => {
			if (
				collectsCarsAndDebt &&
				typeof data.employment.numberOfCars === "undefined"
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "numberOfCars"],
				});
			}
		}) satisfies z.ZodType<EmploymentCarsFields>;
};

export const useDebtPartialSchema = () =>
	z.object({
		debts: z.object({
			mortgageLoan: z.number().min(0).max(100000000).optional(),
			vehicleLoan: z.number().min(0).max(100000000).optional(),
			studentLoan: z.number().min(0).max(100000000).optional(),
			consumerLoan: z.number().min(0).max(100000000).optional(),
		}),
	}) satisfies z.ZodType<DebtEconomyFields>;

export const useResidencePartialSchema = () => {
	const t = useTranslations("forms.errors.general");

	return z
		.object({
			dwelling: z.object({
				type: z.nativeEnum(DwellingType, { message: t("required") }),
				expenses: z.object({
					amount: z.union([
						z.number().min(0).max(100000000),
						z.undefined(),
					]),
					type: z.nativeEnum(ExpenseType),
					period: z.nativeEnum(Period),
				}),
			}),
		})
		.superRefine((data, ctx) => {
			if (
				dwellingRequiresCost(data.dwelling.type) &&
				!z.number().safeParse(data.dwelling.expenses.amount).success
			) {
				ctx.addIssue({
					path: ["dwelling", "expenses", "amount"],
					message: t("required"),
					code: z.ZodIssueCode.custom,
				});
			}
		}) satisfies z.ZodType<ResidenceEconomyFields>;
};

export const useEconomyForm = () => {
	const t = useTranslations("forms.errors.general");
	const tEconomy = useTranslations("forms.errors.economyForm");
	const coApplicantContactSchema = useCoApplicantContactSchema();
	const coApplicantCivilSchema = useCivilApplicantSchema();
	const country = useAppCountry();
	const { collectsCarsAndDebt, minMonthlyIncome: minIncome } =
		COUNTRY_SETTINGS[country];
	const { minDate: minEmploymentStartDate, maxDate: maxEmploymentStartDate } =
		getEmploymentDateBounds();
	const defaultPhoneCountry = useValidationCountry();
	const autofill = isAutofillEnabled(useRuntimeConfig().devConsole);
	const defaultCoApplicant = autofill
		? getMockCoApplicantContact(country)
		: undefined;
	const defaultEmployment = autofill
		? getMockEmploymentDefaults(country)
		: undefined;

	const baseEconomySchema: z.ZodSchema<OnlyEconomyForm> = z
		.object({
			dwelling: z.object({
				type: z.nativeEnum(DwellingType, { message: t("required") }),
				expenses: z.object({
					amount: z.union([
						z.number().min(0).max(100000000),
						z.undefined(),
					]),

					type: z.nativeEnum(ExpenseType),
					period: z.nativeEnum(Period),
				}),
			}),
			employment: z.object({
				type: z.nativeEnum(EmploymentType, { message: t("required") }),
				employer: z.string().optional(),
				startDate: z
					.date({
						message: tEconomy("invalidEmploymentStartDate"),
					})
					.min(minEmploymentStartDate, {
						message: tEconomy("employmentStartDateOutOfRange"),
					})
					.max(maxEmploymentStartDate, {
						message: tEconomy("employmentStartDateOutOfRange"),
					})
					.optional(),
				numberOfCars: z.number().int().min(0).max(100).optional(),
			}),
			income: z.object({
				amount: z
					.number()
					.max(100000000)
					.min(minIncome, { message: tEconomy("incomeTooLow") })
					.optional(),
				type: z.nativeEnum(IncomeType, { message: t("required") }),
				period: z.nativeEnum(Period),
			}),
			debts: z
				.object({
					mortgageLoan: z.number().min(0).max(100000000).optional(),
					vehicleLoan: z.number().min(0).max(100000000).optional(),
					studentLoan: z.number().min(0).max(100000000).optional(),
					consumerLoan: z.number().min(0).max(100000000).optional(),
				})
				.optional(),
		})
		.superRefine((data, ctx) => {
			if (
				dwellingRequiresCost(data.dwelling.type) &&
				!z.number().safeParse(data.dwelling.expenses.amount).success
			) {
				ctx.addIssue({
					path: ["dwelling", "expenses", "amount"],
					message: t("required"),
					code: z.ZodIssueCode.custom,
				});
			}
		});

	const economySchema: z.ZodSchema<OnlyEconomyForm> =
		baseEconomySchema.superRefine((data, ctx) => {
			const occupation = data.employment.type;

			if (typeof data.income.amount !== "number") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["income", "amount"],
				});
			}

			if (
				collectsCarsAndDebt &&
				typeof data.employment.numberOfCars === "undefined"
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "numberOfCars"],
				});
			}

			if (
				employmentRequiresWorkDetails(occupation) &&
				!data.employment.employer
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "employer"],
				});
			}

			if (
				employmentRequiresStartDate(occupation) &&
				!data.employment.startDate
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("required"),
					path: ["employment", "startDate"],
				});
			}
		});

	const coApplicantSchema = z.object({
		applicant: coApplicantContactSchema.and(coApplicantCivilSchema),
		economy: economySchema,
	}) as unknown as z.ZodSchema<CoApplicantForm>;

	const EconomySchema: z.ZodSchema<EconomyForm> = z
		.object({
			applicant: z.object({
				economy: economySchema,
			}),
			coApplicant: z.any().optional(),
			hasCoApplicant: z.boolean().optional(),
		})
		.superRefine((data, ctx) => {
			if (!data.hasCoApplicant) return;
			const { success, error } = coApplicantSchema.safeParse(
				data.coApplicant
			);
			if (!success) {
				error.issues.forEach((issue) => {
					ctx.addIssue({
						path: ["coApplicant", ...issue.path],
						message: issue.message,
						code: z.ZodIssueCode.custom,
					});
				});
			}
		});
	return {
		useForm: useForm<EconomyForm>({
			resolver: zodResolver(
				EconomySchema as unknown as z.ZodType<EconomyForm, EconomyForm>
			),
			// Date/amount errors must surface while typing; the step's
			// Continue button is disabled until the schema passes.
			mode: "onChange",
			defaultValues: {
				applicant: {
					economy: {
						dwelling: {
							expenses: {
								type: ExpenseType.HousingExpenses,
								period: Period.Monthly,
							},
						},
						income: {
							...(defaultEmployment?.income ?? {
								type: IncomeType.GrossIncome,
								period: Period.Monthly,
							}),
						},
						employment: {
							...(defaultEmployment?.employment ?? {}),
						},
						debts: {
							mortgageLoan: undefined,
							vehicleLoan: undefined,
							studentLoan: undefined,
							consumerLoan: undefined,
						},
					},
				},
				coApplicant: {
					...emptyCoApplicant(defaultPhoneCountry),
					applicant: {
						mobileCountry: defaultPhoneCountry,
						...(defaultCoApplicant ?? {}),
					},
					economy: {
						...emptyCoApplicant(defaultPhoneCountry).economy,
						income: {
							...(defaultEmployment?.income ?? {
								type: IncomeType.GrossIncome,
								period: Period.Monthly,
							}),
						},
						employment: {
							...(defaultEmployment?.employment ?? {}),
						},
					},
				},
			},
		}),
		schema: EconomySchema,
	};
};
